import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { jordanGovernorates } from '../data/jordanGovernorates';

const { FiMapPin } = FiIcons;

const JordanMap = ({ experts, onExpertSelect }) => {
  const [hoveredGov, setHoveredGov] = useState(null);
  const getVisualPos = (vx, vy) => ({ x: (vx / 100) * 500, y: (vy / 100) * 600 });
  const approvedExperts = experts.filter(e => e.status === 'approved');

  return (
    <div className="relative w-full h-full bg-slate-50 rounded-xl overflow-hidden border border-slate-200 shadow-inner">
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <div className="bg-white/90 backdrop-blur-md p-3 rounded-xl border border-slate-100 shadow-sm">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Network Grid</h4>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              <span className="text-[10px] font-semibold text-slate-600">Governorate</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-[10px] font-semibold text-slate-600">Active Expert</span>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full h-full flex items-center justify-center p-6">
        <svg viewBox="0 0 500 600" className="w-full h-full max-h-[500px] drop-shadow-2xl overflow-visible">
          {jordanGovernorates.map((gov) => {
            const { x, y } = getVisualPos(gov.visualX, gov.visualY);
            const isHovered = hoveredGov === gov.name;
            const govExperts = approvedExperts.filter(e => e.governorate === gov.name);
            return (
              <g key={gov.name} onMouseEnter={() => setHoveredGov(gov.name)} onMouseLeave={() => setHoveredGov(null)} className="cursor-pointer">
                <motion.circle cx={x} cy={y} r={34} initial={false} animate={{ scale: isHovered ? 1.15 : 1, fill: isHovered ? '#f0fdf4' : '#ffffff', stroke: isHovered ? '#4ade80' : '#f1f5f9' }} className="stroke-2" />
                <circle cx={x} cy={y} r="4" className="fill-blue-600" />
                <text x={x} y={y + 16} textAnchor="middle" className={`text-[12px] font-black tracking-tighter transition-all duration-300 ${isHovered ? 'fill-green-700' : 'fill-slate-400'}`}>
                  {gov.name.toUpperCase()}
                </text>
                {govExperts.length > 0 && (
                  <g transform={`translate(${x + 22}, ${y - 22})`}>
                    <circle r="10" className="fill-green-600" />
                    <text textAnchor="middle" dy="3.5" className="fill-white text-[11px] font-black">{govExperts.length}</text>
                  </g>
                )}
                {govExperts.map((exp, idx) => {
                  const angle = (idx / govExperts.length) * Math.PI * 2;
                  const dotX = x + Math.cos(angle) * 24;
                  const dotY = y + Math.sin(angle) * 24;
                  return (
                    <motion.circle key={exp.id} whileHover={{ scale: 1.4 }} cx={dotX} cy={dotY} r="6" onClick={(e) => { e.stopPropagation(); onExpertSelect(exp); }} className="fill-green-500 stroke-white stroke-[2px] transition-colors">
                      <title>{exp.full_name}</title>
                    </motion.circle>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>

      <AnimatePresence>
        {hoveredGov && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white px-6 py-3 rounded-2xl shadow-2xl border border-slate-100 flex items-center space-x-6 min-w-[240px] z-20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiMapPin} className="text-blue-600 text-lg" />
              </div>
              <div className="leading-tight">
                <div className="font-black text-slate-800 text-base">{hoveredGov}</div>
                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Governorate</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default JordanMap;